/**
 * Planning V1 data access. No Planning table: this composes the existing
 * canonical reads.
 *
 *  - Projects: loadScopedActiveProjects (organization scope, employee
 *    visibility, projects_operational for employees, archived-client lock).
 *  - Locations / Features / Tasks: organization-scoped, is_active rows,
 *    narrowed to the visible project ids (RLS already scopes by organization;
 *    the project id filter keeps another project's rows out of the screen).
 *  - Offline / failed fetch: the device copies the app already keeps (field
 *    project snapshot + cached project tasks). Read-only; nothing is written.
 */
import { supabase } from "../../supabase";
import { assertCan, CAPABILITY } from "../permissions";
import { isOffline } from "../connectivity";
import { shouldSkipSupabase } from "../guestMode";
import { resolveOrganizationIdForQueries } from "../orgQueryScope";
import { loadScopedActiveProjects } from "../operationalQueryAdapters";
import { readCachedFieldLocations, readCachedFieldProjects } from "../projectFieldSnapshot";
import { readCachedProjectTasks } from "../tasks/tasksCache";
import { isTasksUnavailableError } from "../tasks/projectTasks";

const PROJECT_SELECT =
  "project_id, project_name, project_number, status, is_active, client_id, organization_id, estimated_end_date";
const CHUNK = 60;

function chunk(ids) {
  const out = [];
  for (let i = 0; i < ids.length; i += CHUNK) out.push(ids.slice(i, i + CHUNK));
  return out;
}

async function selectForProjects(table, organizationId, projectIds, orderColumn = "sort_order") {
  const rows = [];
  for (const ids of chunk(projectIds)) {
    const { data, error } = await supabase
      .from(table)
      .select("*")
      .eq("organization_id", organizationId)
      .eq("is_active", true)
      .in("project_id", ids)
      .order(orderColumn, { ascending: true });
    if (error) throw error;
    rows.push(...(data || []));
  }
  return rows;
}

function fromDevice(actor) {
  const projects = readCachedFieldProjects(actor);
  const locations = [];
  const features = [];
  const tasks = [];
  let hasTaskCopy = false;
  projects.forEach((project) => {
    (readCachedFieldLocations(actor, project.project_id) || []).forEach((location) => {
      locations.push({ ...location, project_id: location.project_id || project.project_id });
      (location.features || []).forEach((feature) => {
        features.push({
          ...feature,
          project_id: project.project_id,
          project_location_id: location.project_location_id,
        });
      });
    });
    const cachedTasks = readCachedProjectTasks(actor, project.project_id);
    if (cachedTasks) {
      hasTaskCopy = true;
      tasks.push(...cachedTasks);
    }
  });
  return { projects, locations, features, tasks, fromDevice: true, hasTaskCopy };
}

export async function loadPlanningData(actor) {
  assertCan(actor, CAPABILITY.SCREEN_PLANNING);
  if (shouldSkipSupabase(actor)) {
    return { projects: [], locations: [], features: [], tasks: [], fromDevice: false, tasksAvailable: true };
  }
  if (isOffline()) return { ...fromDevice(actor), tasksAvailable: true };

  try {
    const projects = await loadScopedActiveProjects(supabase, actor, {}, PROJECT_SELECT, {
      activeOnly: true,
    });
    const ids = projects.map((p) => p.project_id);
    if (ids.length === 0) {
      return { projects: [], locations: [], features: [], tasks: [], fromDevice: false, tasksAvailable: true };
    }
    const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
    if (!organizationId) throw new Error("Organization is required.");

    const [locations, features] = await Promise.all([
      selectForProjects("project_locations", organizationId, ids),
      selectForProjects("project_features", organizationId, ids),
    ]);

    let tasks = [];
    let tasksAvailable = true;
    try {
      tasks = await selectForProjects("project_tasks", organizationId, ids);
    } catch (error) {
      if (!isTasksUnavailableError(error)) throw error;
      tasksAvailable = false;
    }
    return { projects, locations, features, tasks, fromDevice: false, tasksAvailable };
  } catch (error) {
    const device = fromDevice(actor);
    if (device.projects.length) return { ...device, tasksAvailable: true };
    throw error;
  }
}
